import React, {useEffect} from 'react'
import {Box, Button, Flex, Grid, Link, Stack, Text, useMultiStyleConfig} from '@chakra-ui/react'
import {defineMessage, useIntl, FormattedMessage} from 'react-intl'
import ProductRegistrationHeader from './partials/product-registration-header'
import ProductRegistrationProducts from './partials/product-registration-products'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {useProductSearch} from '@salesforce/commerce-sdk-react'
import {useLocation} from 'react-router-dom'
import routingObject from '../../utils/static-routes'
import {useHistory} from 'react-router-dom/cjs/react-router-dom.min'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import LoadingSpinner from '../../components/loading-spinner'
import {useGoogleTagManager} from '../../custom-api/hooks/use-google-tag-manager'
import {registration_event} from '../../custom-api/layers/product/registration'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {Helmet} from 'react-helmet'
import {isSerialNumberRegistrationCategory} from '../../custom-hooks/use-product-registration-data'
import useCurrentHost from '../../hooks/use-current-host'

const ProductRegistrationSelect = () => {
    const styles = useMultiStyleConfig('ProductRegistration', {variant: 'productSearch'})
    const {getHost} = useCurrentHost()
    const location = useLocation()
    const navigate = useNavigation()
    const history = useHistory()
    const {site, locale, buildUrl} = useMultiSite()
    const {currency} = useCurrency()

    const queryParams = new URLSearchParams(location.search)
    const textParam = queryParams.get('text')
    const snParam = queryParams.get('sn')
    const categoryParam = queryParams.get('category')
    const getPersistedCategory = () => {
        if (categoryParam) {
            return categoryParam
        }

        if (typeof window !== 'undefined') {
            return localStorage.getItem('productRegistrationSearchCategory')
        }

        return null
    }
    const persistedCategory = getPersistedCategory()
    const isSNVariant = isSerialNumberRegistrationCategory(persistedCategory)
    const searchBackPath = `${routingObject.PRODUCT_REGISTRATION_PATH}/search`

    // Keep the URL's text value intact while applying the catalog's established search format.
    const searchQuery =
        isSNVariant && /^\d{13}$/.test(textParam || '')
            ? `${textParam.substring(0, 12)}*`
            : isSNVariant && textParam && !textParam.endsWith('*')
            ? `${textParam}*`
            : textParam

    const {customParams} = getConfig()
    const currentPath = 'https://' + customParams.currentHost + location.pathname
    const intl = useIntl()
    const productRegistrationTitleMessage = defineMessage({
        id: 'product_registration.title',
        defaultMessage: 'Register your product',
    })

    const productRegistrationSubtitleMessage = defineMessage({
        id: 'product_registration.subtitle',
        defaultMessage: 'Register your product to unlock a world of benefits.',
    })
    const webPageSchemaMarkup = {
        '@context': 'https://schema.org/',
        '@type': 'WebPage',
        name: intl.formatMessage(productRegistrationTitleMessage),
        description: intl.formatMessage(productRegistrationSubtitleMessage),
        url: currentPath,
    }

    useEffect(() => {
        if (!categoryParam) return

        localStorage.setItem('productRegistrationSearchCategory', categoryParam)
        queryParams.delete('category')
        history.replace({search: queryParams.toString()})
    }, [categoryParam])

    useEffect(() => {
        if (snParam) {
            localStorage.setItem('prepopulatedSerialNumber', snParam)
        }
    }, [snParam])

    const {data: productSearchResult, isFetching} = useProductSearch(
        {
            parameters: {
                q: searchQuery,
                expand: ['represented_products', 'images', 'page_meta_tags'],
                refine: ['c_enabledForReg=true'],
                locale: locale.id,
                site: site.id,
                currency,
                c_isProductReg: true,
            },
        },
        {
            enabled: !!searchQuery,
            keepPreviousData: false,
        },
    )

    const representedHits = productSearchResult?.hits?.filter(
        (product) => product?.representedProduct?.id && product?.productType?.set !== true,
    )

    // Retry logic: if no results, try different dot placements
    useEffect(() => {
        if (!isSNVariant && !isFetching && textParam) {
            const normalizedQuery = textParam.replace(/\./g, '')

            // Check if we're in a retry sequence
            const storedBaseQuery = sessionStorage.getItem('productRegistrationBaseQuery')
            const attempts = parseInt(
                sessionStorage.getItem('productRegistrationAttempts') || '0',
                10,
            )

            // If this is a new product code (first search), store it
            if (storedBaseQuery !== normalizedQuery) {
                sessionStorage.setItem('productRegistrationBaseQuery', normalizedQuery)
                sessionStorage.setItem('productRegistrationAttempts', '0')
                sessionStorage.removeItem('productRegistrationTriedWithoutDot')
                // Only proceed if new search has no results
                if (representedHits?.length > 0) {
                    return
                }
                // Fall through to retry logic below
            }

            // If we found results, stop retrying
            if (representedHits?.length > 0) {
                return
            }

            const maxPositions = Math.min(normalizedQuery.length - 1, 15)

            // Get current attempt number
            let currentAttempt = attempts
            try {
                currentAttempt = parseInt(
                    sessionStorage.getItem('productRegistrationAttempts') || '0',
                    10,
                )
            } catch {
                currentAttempt = 0
            }

            // Case 1: Still have positions to try - keep trying all positions until finding product
            if (currentAttempt < maxPositions) {
                const nextAttempt = currentAttempt + 1

                // Record this attempt
                try {
                    sessionStorage.setItem('productRegistrationAttempts', String(nextAttempt))
                } catch {
                    // sessionStorage not available
                }

                const newQuery =
                    normalizedQuery.slice(0, nextAttempt) + '.' + normalizedQuery.slice(nextAttempt)

                // Redirect to try this position
                const redirectUrl = `${
                    routingObject.PRODUCT_REGISTRATION_PATH
                }/select?text=${encodeURIComponent(newQuery)}`
                history.push(redirectUrl)
            } else if (!sessionStorage.getItem('productRegistrationTriedWithoutDot')) {
                // Case 2: Tried all positions, now try without any dot as final fallback
                sessionStorage.setItem('productRegistrationTriedWithoutDot', 'true')
                const redirectUrl = `${
                    routingObject.PRODUCT_REGISTRATION_PATH
                }/select?text=${encodeURIComponent(normalizedQuery)}`
                history.push(redirectUrl)
            }
        }
    }, [isSNVariant, isFetching, textParam, representedHits, history])

    // Clear retry state when we get results
    useEffect(() => {
        if (representedHits?.length > 0) {
            try {
                sessionStorage.removeItem('productRegistrationAttempts')
                sessionStorage.removeItem('productRegistrationBaseQuery')
                sessionStorage.removeItem('productRegistrationTriedWithoutDot')
            } catch {
                // sessionStorage not available
            }
        }
    }, [representedHits?.length])

    const variant =
        representedHits?.length > 2
            ? 'default'
            : representedHits?.length === 2
            ? 'doubleProducts'
            : 'singleProduct'
    const productsVariant = useMultiStyleConfig('ProductRegistrationProducts', {variant})

    const handleButtonClick = (product) => {
        const registrationEan =
            isSNVariant && !snParam && /^(?:\d{8}|\d{13})$/.test(textParam || '')
                ? textParam
                : undefined
        localStorage.setItem('selectedProduct', JSON.stringify({...product, registrationEan}))
        const productId = product?.representedProduct?.id
        navigate(`${routingObject.PRODUCT_REGISTRATION_PATH}/user-detail?code=${productId}`)
    }

    const redirectionPath = `${getHost()}${buildUrl(
        '/product-registration/search?category=Legacy',
    )}`
    const {addLayer} = useGoogleTagManager()
    useEffect(() => {
        if (representedHits && !isFetching) {
            addLayer(
                registration_event.bind(this, {
                    event: 'productRegistration',
                    eventCategory: 'afterSales',
                    eventAction: 'productRegistration',
                    eventLabel: 'select',
                    productRegistration: {
                        products: representedHits.map((product) => {
                            return {
                                id: product.representedProduct?.id, // product SKU
                                name: product.c_defaultName, // product name (English)
                                category: product.c_subCategoryDefaultName, // product category (English)
                            }
                        }),
                    },
                }),
            )
        }
    }, [representedHits, isFetching])

    return (
        <Flex {...styles.container} layerStyle={'page'}>
            <Helmet>
                <script type="application/ld+json">
                    {JSON.stringify(webPageSchemaMarkup, null, 2)}
                </script>
            </Helmet>
            <Flex {...styles.headerStepperContainer}>
                <ProductRegistrationHeader index={1} variant={'product'} />
            </Flex>
            {isFetching ? (
                <LoadingSpinner />
            ) : (
                <>
                    {representedHits?.length > 0 ? (
                        <>
                            <Stack width={'100%'} gap={['40px', '40px', '40px', '80px']}>
                                <Box width={'100%'}>
                                    <Grid {...productsVariant.productdGrid}>
                                        {representedHits?.map((product) => (
                                            <ProductRegistrationProducts
                                                key={product.productId}
                                                product={product}
                                                variant={variant}
                                                onClick={() => handleButtonClick(product)}
                                            />
                                        ))}
                                    </Grid>
                                </Box>
                                <Flex direction="column" gap="4px" mt="16px">
                                    <Text textStyle="body-small" color="text.default">
                                        <FormattedMessage
                                            id="product_registration.look_like_your_product"
                                            defaultMessage="None of these look like your product?"
                                        />
                                    </Text>
                                    <Link
                                        textStyle="body-small"
                                        color="button.text.text-default"
                                        fontWeight={'bold'}
                                        onClick={() => navigate(searchBackPath)}
                                        cursor="pointer"
                                    >
                                        <FormattedMessage
                                            id="product_registration.go_back_to_product_search"
                                            defaultMessage="Go back to product search"
                                        />
                                    </Link>
                                </Flex>
                            </Stack>
                        </>
                    ) : (
                        <>
                            <Flex {...styles.productNotFoundInnerContainer}>
                                <Text {...styles.labelSearchTitle}>
                                    <FormattedMessage
                                        id="product_registration.product_not_found.title"
                                        defaultMessage="No products found"
                                    />
                                </Text>
                                <Text {...styles.subtitle}>
                                    <FormattedMessage
                                        id="product_registration.product_not_found.description"
                                        defaultMessage="Please go back and try retyping the SKU or Model Number."
                                    />
                                </Text>
                                <Button
                                    {...styles.tryAgainButton}
                                    variant="primary"
                                    as={Link}
                                    href={redirectionPath}
                                >
                                    <FormattedMessage
                                        defaultMessage="Try again"
                                        id="product_registration.product_not_found.try_again_button"
                                    />
                                </Button>
                            </Flex>
                        </>
                    )}
                </>
            )}
        </Flex>
    )
}

export default ProductRegistrationSelect
